import { Injectable } from '@angular/core';

import { DateTimeFormats, DateUtil } from './date/date-util';

/** One of the three format kinds a locale carries, see {@link DateTimeFormats}. */
export type DateFormatKind = 'date' | 'time' | 'seconds';

interface DateServiceConfig {
  globalLocale: string;
  customFormats: DateTimeFormats;
}

/**
 * The configuration behind the statics of {@link DateService}. Stored on `globalThis` so that every bundled copy of this package on the page shares
 * it: the first copy to load creates it, later ones adopt it. An incompatible value in the global slot falls back to a bundle-local configuration.
 */
const config: DateServiceConfig = adoptOrCreateConfig();

function adoptOrCreateConfig(): DateServiceConfig {
  const slot = globalThis as { CelumDateServiceConfig?: unknown };
  const existing = slot.CelumDateServiceConfig;

  if (!existing) {
    return (slot.CelumDateServiceConfig = createDefaultConfig());
  }

  if (isDateServiceConfig(existing)) {
    return existing;
  }

  console.warn('DateService: incompatible value in global "CelumDateServiceConfig" - falling back to the default configuration for this bundle.');
  return createDefaultConfig();
}

function createDefaultConfig(): DateServiceConfig {
  return { globalLocale: 'en', customFormats: structuredClone(DateUtil.DEFAULT_FORMATS) };
}

/** Whether a value found on `globalThis` can serve as configuration: it has to carry what this copy dereferences, whatever else it may hold. */
function isDateServiceConfig(value: unknown): value is DateServiceConfig {
  const candidate = value as Partial<DateServiceConfig> | null;
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    typeof candidate.globalLocale === 'string' &&
    typeof candidate.customFormats === 'object' &&
    candidate.customFormats !== null &&
    !!candidate.customFormats.en
  );
}

/**
 * Date formatting and parsing based on the currently selected locale. Depends on some other service to keep {@link DateService.GLOBAL_LOCALE} in sync
 * with the selected locale.
 *
 * Custom date and time formats are used as defaults for its methods and can/should be set from some global service during application startup - one
 * {@link setCustomFormat} call per locale and format kind, read back as a whole via {@link getCustomFormats}.
 *
 * If you do not depend on custom formats and/or you do not want to rely on {@link DateService.GLOBAL_LOCALE} to be in sync with the current selected locale,
 * consider using {@link DateUtil} instead which has default formats and always expects a locale to be passed.
 */
@Injectable({ providedIn: 'root' })
export class DateService {
  public static get GLOBAL_LOCALE(): string {
    return config.globalLocale;
  }

  public static set GLOBAL_LOCALE(locale: string) {
    config.globalLocale = locale;
  }

  /** All custom formats, keyed by locale. Returns a copy - use {@link setCustomFormat} to change them. */
  public static getCustomFormats(): DateTimeFormats {
    return structuredClone(config.customFormats);
  }

  /**
   * Set the custom format of one locale and format kind, e.g. `setCustomFormat('en', 'date', 'MM/dd/yyyy')`. A falsy format is ignored, so an unset
   * customer property can be passed straight through - the shipped default stays in place.
   */
  public static setCustomFormat(locale: string, kind: DateFormatKind, format: string): void {
    if (format) {
      config.customFormats[locale] = { ...(config.customFormats[locale] ?? config.customFormats.en), [kind]: format };
    }
  }

  /**
   * @param date          the date to format
   * @param currentLocale the current locale, provide "undefined", if you want to fallback to the current locale
   * @param format        optional define the format to use (default is numeric)
   * @param showTime      whether to show the time as well (default: only the date)
   * @param showSeconds   whether to show the seconds as well (default: only hours and minutes, if showTime is true)
   */
  public static formatDateForGivenLocale(
    date: number,
    currentLocale: string = DateService.GLOBAL_LOCALE,
    format: string = DateUtil.FORMATS.DATE.NUMERIC,
    showTime = false,
    showSeconds = false
  ): string {
    return DateUtil.handleDateFormat(new Date(date), format, config.customFormats, currentLocale, showTime, showSeconds);
  }

  /**
   * Format the given date as UTC date. This is useful if you want to keep the date in UTC, even if the date is in your local timezone.
   * E.g. UTC "15.3.2024 00:00" would be in your timezone "14.3.2024 22:00", but you want to keep it at 15.3.2024.
   *
   * Time information is cut off.
   *
   * @param date          the date to format
   * @param currentLocale the current locale, provide "undefined", if you want to fallback to the current locale
   * @param format        optional define the format to use (default is numeric)
   */
  public static formatDateForGivenLocaleAsUTCDate(
    date: number,
    currentLocale: string = DateService.GLOBAL_LOCALE,
    format: string = DateUtil.FORMATS.DATE.NUMERIC
  ): string {
    return DateUtil.handleDateFormatAsUTC(new Date(date), format, config.customFormats, currentLocale);
  }

  public static addTimeToGivenFormat(format: string, locale: string = DateService.GLOBAL_LOCALE): string {
    const localeToUse = config.customFormats[locale] ? locale : 'en';

    return `${format} ${config.customFormats[localeToUse].time}`;
  }

  public static format(date: number, format: string, showTime = false): string {
    return DateUtil.handleDateFormat(new Date(date), format, config.customFormats, DateService.GLOBAL_LOCALE, showTime);
  }

  public static handleDateFormat(date: Date, format: string, locale: string = DateService.GLOBAL_LOCALE, showTime?: boolean): string {
    return DateUtil.handleDateFormat(date, format, config.customFormats, locale, showTime);
  }

  public static convertToDateNumeric(value: string, locale: string = DateService.GLOBAL_LOCALE): Date {
    return DateUtil.convertToDateNumeric(value, config.customFormats, locale);
  }

  public static getFormat(localeString?: string, withTime = false): string {
    const locale = localeString ? localeString : DateService.GLOBAL_LOCALE;
    return DateUtil.getFormat(locale, config.customFormats, withTime);
  }
}
